import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { AdminFactorService } from '../../services/admin-factor.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import {
  AdminCustomFactor,
  EmissionFactorCategory,
} from 'src/app/factors/types/factor';

@Component({
  selector: 'app-manage-factors',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TagModule,
    SelectModule,
    TooltipModule,
    ToastModule,
  ],
  templateUrl: './manage-factors.component.html',
})
export class ManageFactorsComponent implements OnInit {
  private adminFactorService = inject(AdminFactorService);
  private notificationService = inject(NotificationService);

  factors: AdminCustomFactor[] = [];
  globalFilter = '';
  validatedFilter: boolean | null = null;
  categoryFilter: string | null = null;
  companyFilter: string | null = null;

  validatedOptions = [
    { label: 'All', value: null },
    { label: 'Validated', value: true },
    { label: 'Not validated', value: false },
  ];

  categoryOptions = [
    { label: 'All Categories', value: null },
    { label: 'Transport', value: EmissionFactorCategory.Transport },
    { label: 'Accommodation', value: EmissionFactorCategory.Accommodation },
    { label: 'Activity', value: EmissionFactorCategory.Activity },
  ];

  get companyOptions() {
    const companies = [
      ...new Map(
        this.factors.map((f) => [f.company.id, f.company.name]),
      ).entries(),
    ];
    return [
      { label: 'All Companies', value: null },
      ...companies.map(([id, name]) => ({ label: name, value: id })),
    ];
  }

  get filteredFactors(): AdminCustomFactor[] {
    let result = this.factors;

    if (this.validatedFilter !== null) {
      result = result.filter((f) => f.validated === this.validatedFilter);
    }
    if (this.categoryFilter) {
      result = result.filter((f) => f.category === this.categoryFilter);
    }
    if (this.companyFilter) {
      result = result.filter((f) => f.company.id === this.companyFilter);
    }

    return result;
  }

  ngOnInit() {
    this.loadFactors();
  }

  loadFactors() {
    this.adminFactorService.getAllFactors().subscribe({
      next: (factors) => (this.factors = factors),
      error: () =>
        this.notificationService.error('Error loading emission factors'),
    });
  }

  resetFilters() {
    this.globalFilter = '';
    this.validatedFilter = null;
    this.categoryFilter = null;
    this.companyFilter = null;
  }

  toggleValidation(factor: AdminCustomFactor) {
    const newValue = !factor.validated;
    this.adminFactorService.setValidated(factor.id, newValue).subscribe({
      next: (updated) => {
        const idx = this.factors.findIndex((f) => f.id === updated.id);
        if (idx !== -1) this.factors[idx] = updated;
        this.notificationService.success(
          newValue ? 'Factor validated' : 'Factor validation removed',
        );
      },
      error: () =>
        this.notificationService.error('Error updating factor validation'),
    });
  }

  getCategoryTag(category: string): {
    label: string;
    classes: string;
    icon: string;
  } {
    switch (category) {
      case EmissionFactorCategory.Transport:
        return {
          label: 'Transport',
          classes: 'bg-blue-50 text-blue-800 border-blue-200',
          icon: 'pi pi-car',
        };
      case EmissionFactorCategory.Accommodation:
        return {
          label: 'Accommodation',
          classes: 'bg-purple-50 text-purple-800 border-purple-200',
          icon: 'pi pi-building',
        };
      case EmissionFactorCategory.Activity:
        return {
          label: 'Activity',
          classes: 'bg-orange-50 text-orange-800 border-orange-200',
          icon: 'pi pi-flag',
        };
      default:
        return {
          label: category,
          classes: 'bg-greyscale-50 text-greyscale-700 border-greyscale-200',
          icon: 'pi pi-question',
        };
    }
  }

  formatValue(factor: AdminCustomFactor): string {
    if (factor.valuePerKm != null) return `${factor.valuePerKm} kg/km`;
    if (factor.valuePerH != null) return `${factor.valuePerH} kg/h`;
    if (factor.valuePerNight != null)
      return `${factor.valuePerNight} kg/night`;
    return '--';
  }

  formatDate(date: string | Date): string {
    if (!date) return '--';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
